<script setup>
/* global axios */
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useAlert } from 'dashboard/composables';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';

const route = useRoute();
const { t } = useI18n();
const accountId = computed(() => route.params.accountId);

const featureEnabled = ref(false);
const loading = ref(true);
const currentPrompt = ref('');
const sessionId = ref('');
const messages = ref([]);
const userInput = ref('');
const sending = ref(false);
const conversationDone = ref(false);
const generatedPrompt = ref('');
const finalizing = ref(false);
const lastGeneratedAt = ref('');

onMounted(async () => {
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${accountId.value}/ai_agent_settings`
    );
    featureEnabled.value = data.enabled;
    currentPrompt.value = data.prompt || '';
    lastGeneratedAt.value = data.last_generated_at || '';
  } catch (error) {
    console.error('Failed to load settings:', error);
  } finally {
    loading.value = false;
  }
});

const startConversation = async () => {
  try {
    sending.value = true;
    const { data } = await axios.post(
      `/api/v1/accounts/${accountId.value}/ai_agent_settings/start`
    );
    sessionId.value = data.session_id;
    messages.value.push({ role: 'assistant', content: data.message });
    conversationDone.value = data.done;
  } catch (error) {
    useAlert('Failed to start conversation');
  } finally {
    sending.value = false;
  }
};

const sendMessage = async () => {
  if (!userInput.value.trim()) return;

  const message = userInput.value;
  messages.value.push({ role: 'user', content: message });
  userInput.value = '';
  sending.value = true;

  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${accountId.value}/ai_agent_settings/message`,
      { session_id: sessionId.value, message }
    );
    messages.value.push({ role: 'assistant', content: data.message });
    conversationDone.value = data.done;
  } catch (error) {
    useAlert('Failed to send message');
  } finally {
    sending.value = false;
  }
};

const generatePrompt = async () => {
  finalizing.value = true;
  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${accountId.value}/ai_agent_settings/finalize`,
      { session_id: sessionId.value }
    );
    generatedPrompt.value = data.prompt;
    currentPrompt.value = data.prompt;
    lastGeneratedAt.value = new Date().toISOString();
    useAlert('Prompt generated and synced to your AI agent!');
  } catch (error) {
    useAlert('Failed to generate prompt');
  } finally {
    finalizing.value = false;
  }
};

const saveEditedPrompt = async () => {
  finalizing.value = true;
  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${accountId.value}/ai_agent_settings/finalize`,
      {
        session_id: sessionId.value,
        edited_prompt: generatedPrompt.value,
      }
    );
    currentPrompt.value = data.prompt;
    lastGeneratedAt.value = new Date().toISOString();
    useAlert('Prompt updated and synced!');
  } catch (error) {
    useAlert('Failed to save prompt');
  } finally {
    finalizing.value = false;
  }
};

const regenerate = () => {
  messages.value = [];
  sessionId.value = '';
  generatedPrompt.value = '';
  conversationDone.value = false;
};

const formattedLastGeneratedAt = computed(() => {
  if (!lastGeneratedAt.value) {
    return '';
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(lastGeneratedAt.value));
});

const responsesCount = computed(
  () => messages.value.filter(message => message.role === 'user').length
);

const wizardStepLabel = computed(() => {
  if (generatedPrompt.value) {
    return 'Review prompt';
  }

  if (sessionId.value && conversationDone.value) {
    return 'Generate prompt';
  }

  if (sessionId.value) {
    return 'Answer questions';
  }

  return 'Start setup';
});

const googleDriveSettingsUrl = computed(() =>
  frontendURL(`accounts/${accountId.value}/settings/integrations/google_drive`)
);
</script>

<template>
  <SettingsLayout class="w-full max-w-5xl mx-auto">
    <template #header>
      <BaseSettingsHeader
        :title="t('AI_AGENT_SETTINGS.TITLE')"
        :description="t('AI_AGENT_SETTINGS.DESCRIPTION')"
        feature-name="ai-agent-config"
      />
    </template>

    <template #body>
      <div v-if="loading" class="flex justify-center p-8">
        <div class="text-gray-600">Loading...</div>
      </div>

      <div v-else class="space-y-6">
        <div
          v-if="!featureEnabled"
          class="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
        >
          AI prompt generation is currently disabled by your administrator.
        </div>

        <div class="rounded-xl border border-n-weak bg-n-card p-5 shadow-sm space-y-4">
          <div class="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 class="text-lg font-semibold text-gray-900">
                Build your AI behavior in minutes
              </h3>
              <p class="text-sm text-gray-600 mt-1">
                Current step: <span class="font-medium">{{ wizardStepLabel }}</span>
              </p>
            </div>
            <span
              class="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700"
            >
              {{ responsesCount }} answers collected
            </span>
          </div>
          <div class="grid gap-3 md:grid-cols-2">
            <div class="rounded-lg border border-n-weak bg-n-alpha-2 p-3">
              <p class="text-xs font-semibold uppercase tracking-wide text-n-slate-11">
                Pro tip
              </p>
              <p class="text-sm text-n-slate-12 mt-1">
                Include your support policies, escalation rules, and data you want
                to capture from customers.
              </p>
            </div>
            <div class="rounded-lg border border-blue-100 bg-blue-50 p-3">
              <p class="text-xs font-semibold uppercase tracking-wide text-blue-700">
                Knowledge source
              </p>
              <p class="text-sm text-blue-800 mt-1">
                Connect a Google Drive folder to bring product docs into prompt
                generation.
              </p>
              <router-link
                :to="googleDriveSettingsUrl"
                class="inline-flex items-center mt-2 text-sm font-medium text-blue-700 hover:text-blue-800"
              >
                Configure Google Drive
              </router-link>
            </div>
          </div>
        </div>

        <div
          v-if="featureEnabled && currentPrompt && !sessionId"
          class="rounded-xl border border-n-weak bg-n-card p-5"
        >
          <div class="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h3 class="text-base font-semibold text-n-slate-12">Current AI prompt</h3>
            <span v-if="formattedLastGeneratedAt" class="text-xs text-gray-500">
              Last updated: {{ formattedLastGeneratedAt }}
            </span>
          </div>
          <pre class="text-sm bg-gray-50 p-4 rounded-lg overflow-auto max-h-64 text-gray-800">{{
            currentPrompt
          }}</pre>
          <div class="mt-4 flex flex-wrap gap-2">
            <button
              @click="startConversation"
              class="bg-woot-500 text-white px-4 py-2 rounded-lg hover:bg-woot-600"
            >
              Regenerate prompt
            </button>
            <button
              @click="regenerate"
              class="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200"
            >
              Start fresh
            </button>
          </div>
        </div>

        <div
          v-if="featureEnabled && !sessionId && !currentPrompt"
          class="rounded-xl border border-n-weak bg-n-card p-8 text-center"
        >
          <h3 class="text-lg font-semibold text-n-slate-12">
            Start your setup interview
          </h3>
          <p class="text-sm text-n-slate-11 mt-2 max-w-2xl mx-auto">
            The assistant asks one question at a time and builds a complete system
            prompt tailored to your business.
          </p>
          <button
            @click="startConversation"
            :disabled="sending"
            class="mt-6 bg-woot-500 text-white px-6 py-3 rounded-lg hover:bg-woot-600 disabled:opacity-50"
          >
            {{ sending ? 'Starting...' : 'Start AI Configuration Wizard' }}
          </button>
        </div>

        <div
          v-if="featureEnabled && sessionId && !generatedPrompt"
          class="bg-n-card rounded-xl border border-n-weak overflow-hidden"
        >
          <div class="p-4 border-b border-n-weak bg-n-alpha-2 flex flex-wrap items-center justify-between gap-2">
            <p class="text-sm text-n-slate-11">
              Answer the questions below to create a custom prompt for your AI
              agent
            </p>
            <span class="text-xs text-n-slate-10">{{ responsesCount }} responses</span>
          </div>

          <div class="h-96 overflow-y-auto p-4 space-y-3">
            <div
              v-for="(msg, idx) in messages"
              :key="idx"
              :class="[
                'p-3 rounded-2xl max-w-2xl text-sm leading-6',
                msg.role === 'user'
                  ? 'ml-auto bg-woot-50 text-woot-900 border border-woot-200'
                  : 'mr-auto bg-n-alpha-2 text-n-slate-12 border border-n-weak',
              ]"
            >
              {{ msg.content }}
            </div>
            <div v-if="sending" class="text-n-slate-10 text-sm">
              AI is typing...
            </div>
          </div>

          <div v-if="!conversationDone" class="p-4 border-t border-n-weak bg-n-card">
            <form @submit.prevent="sendMessage" class="flex gap-2">
              <input
                v-model="userInput"
                type="text"
                placeholder="Type your answer..."
                class="flex-1 border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-woot-500/20"
                :disabled="sending"
              />
              <button
                type="submit"
                :disabled="sending || !userInput.trim()"
                class="bg-woot-500 text-white px-6 py-2 rounded-lg hover:bg-woot-600 disabled:opacity-50"
              >
                Send
              </button>
            </form>
          </div>

          <div v-else class="p-4 border-t bg-green-50">
            <p class="text-green-800 mb-3">
              Great! I have all the information needed. Ready to generate your
              custom prompt?
            </p>
            <button
              @click="generatePrompt"
              :disabled="finalizing"
              class="bg-woot-500 text-white px-6 py-2 rounded-lg hover:bg-woot-600 disabled:opacity-50"
            >
              {{ finalizing ? 'Generating...' : 'Generate my AI prompt' }}
            </button>
          </div>
        </div>

        <div
          v-if="featureEnabled && generatedPrompt"
          class="bg-n-card rounded-xl border border-n-weak p-6 space-y-4"
        >
          <div class="flex flex-wrap items-center justify-between gap-2">
            <h3 class="text-xl font-semibold text-n-slate-12">Your generated AI prompt</h3>
            <span class="text-xs text-n-slate-10">Review before saving</span>
          </div>
          <textarea
            v-model="generatedPrompt"
            class="w-full h-72 border border-n-weak bg-n-background text-n-slate-12 rounded-lg p-4 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-woot-500/20"
          />
          <div class="flex flex-wrap gap-2">
            <button
              @click="saveEditedPrompt"
              :disabled="finalizing"
              class="bg-woot-500 text-white px-6 py-2 rounded-lg hover:bg-woot-600 disabled:opacity-50"
            >
              {{ finalizing ? 'Saving...' : 'Save and apply to AI agent' }}
            </button>
            <button
              @click="regenerate"
              class="bg-n-alpha-2 text-n-slate-12 px-6 py-2 rounded-lg hover:bg-n-alpha-3"
            >
              Start over
            </button>
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
